/**
 * The Journal destination, composed for the real app.
 *
 * Every piece the Journal needs existed before this file and none of them was
 * mounted outside a test harness, so the shipped build had a Journal that could
 * render a timeline and do nothing else: `JournalHomeHostProvider`,
 * `JournalComposerSessionProvider`, `JournalReaderPresentationProvider` and
 * `JournalGoalsPresentationProvider` were absent, which under §8.6 makes Write,
 * Continue writing, Goals, row-tap and recovery export **inert labels** — the
 * honest rendering of "not composed here", and exactly what this module stops
 * being true. `JournalComposerHostProvider` is mounted here too: without it the
 * composer has no `onDismiss`, and a composer that cannot dismiss itself stays
 * on screen after the entry it just saved is durable.
 *
 * Three things decide the shape of what follows.
 *
 * **The store is the durable one.** `createJournalRepository` needs a
 * `JournalFileStore`, and `MemoryJournalFileStore` — the only one that needs no
 * platform — forgets everything on reload. So the substrate is OPFS
 * ({@link openOpfsJournalFileStore}), reached through {@link
 * openBrowserJournalFiles} because opening it is asynchronous and React
 * composition is not (see that function for why the deferral is a store rather
 * than a loading state).
 *
 * **Order is not arbitrary.** The repository is above the privacy session,
 * because the session is constructed with a loader that reads it; the privacy
 * session is above the composer session, which gates every `begin…` on the
 * vault; the three presentations are above the hosts, which are the only things
 * that call `present()`. Any other order produces a provider that is mounted but
 * cannot see what it needs.
 *
 * **The hosts are wiring, not policy.** Every rule they touch — which draft is
 * freshest, whether a favourite writes to the draft or the entry, what a
 * refused gate says — already lives in the feature that owns it. What is left
 * here is who calls whom, which is precisely the part no feature packet was
 * allowed to write.
 */

import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import type {
  JournalEntryProjection,
  JournalRecoveryItem,
} from "@ff/domain";
import type { JournalRepositoryPort } from "@ff/domain/ports";
import type { JournalFileRead, JournalFileStore } from "@ff/store";
import { createJournalRepository, openOpfsJournalFileStore } from "@ff/store";

// Imported file by file rather than through each feature's barrel, the way
// `AppRoot` already reaches `journalData`. A barrel import would pull the
// timeline, the rich editor and the goals workspace into the eagerly loaded
// composition root and undo the route table's lazy loading of the Journal chunk.
import type { JournalComposerHost } from "../features/journal/composer/host";
import { JournalComposerHostProvider } from "../features/journal/composer/host";
import type { JournalComposerSessionState } from "../features/journal/composer/session";
import {
  JournalComposerSessionProvider,
  useOptionalJournalComposerSession,
} from "../features/journal/composer/session";
import type { JournalGoalsPresentationState } from "../features/journal/goals/presentation";
import {
  JournalGoalsPresentationProvider,
  useOptionalJournalGoalsPresentation,
} from "../features/journal/goals/presentation";
import type { JournalHomeHost } from "../features/journal/home/host";
import { JournalHomeHostProvider } from "../features/journal/home/host";
import {
  JournalRepositoryProvider,
  useJournalLibraryLoader,
} from "../features/journal/home/journalData";
import { deleteJournalEntry, toggleJournalFavorite } from "../features/journal/home/mutations";
import type { JournalReaderPresentationState } from "../features/journal/reader/presentation";
import {
  JournalReaderPresentationProvider,
  useOptionalJournalReaderPresentation,
} from "../features/journal/reader/presentation";
import { exportPreservedCopy } from "../features/journal/reader/recoveryExport";
import { PrivacySessionProvider, usePrivacySession } from "../privacy";
import { useJournalComposerIdentity } from "./identity";
import { useClock, useDigest, useExporter, useIds } from "./ports";

/* ------------------------------------------------------------------------- *
 * The durable substrate
 * ------------------------------------------------------------------------- */

/**
 * A `JournalFileStore` that opens the origin private file system on first use.
 *
 * `openOpfsJournalFileStore()` is asynchronous — OPFS hands out handles, not
 * paths — and everything above it is not: `createJournalRepository` is a
 * constructor, `JournalRepositoryProvider` takes a value, and the Journal's
 * library states are `preparing | unavailable | concealed | revealed` with no
 * arm for "the substrate is still being opened". Resolving the store in an
 * effect and holding `null` until it lands would spend the first frames of every
 * cold start rendering F-JR-03's **unavailable** copy — the sentence that means
 * "this build cannot open your journal" — over a journal that opens fine a tick
 * later. That is the one thing `LibraryStatus` exists to prevent.
 *
 * Deferring inside the store instead keeps the whole stack synchronous and puts
 * the wait where every caller already awaits. Failure needs no special handling
 * either: `openOpfsJournalFileStore` rejects with a `JournalFileError` carrying
 * `repositoryUnavailable`, which is the code the repository would have reported
 * anyway, so an origin with no OPFS reaches the user as the same sentence it
 * always did.
 *
 * The open is cached only while it is pending or has succeeded. A cached
 * rejection would make F-JR-03's **Retry** button unable to change anything for
 * the life of the tab, which is a button that lies about what it does.
 */
class DeferredOpfsJournalFileStore implements JournalFileStore {
  #opening: Promise<JournalFileStore> | null = null;

  async #store(): Promise<JournalFileStore> {
    const opening = this.#opening ?? openOpfsJournalFileStore();
    this.#opening = opening;
    try {
      return await opening;
    } catch (error) {
      // Only if nothing has replaced it: a concurrent caller may already have
      // started a fresh attempt, and clearing that one would open a third.
      if (this.#opening === opening) this.#opening = null;
      throw error;
    }
  }

  async createDirectory(path: string): Promise<void> {
    return (await this.#store()).createDirectory(path);
  }

  async directoryExists(path: string): Promise<boolean> {
    return (await this.#store()).directoryExists(path);
  }

  async list(directory: string): Promise<readonly string[]> {
    return (await this.#store()).list(directory);
  }

  async fileExists(path: string): Promise<boolean> {
    return (await this.#store()).fileExists(path);
  }

  async read(path: string): Promise<JournalFileRead> {
    return (await this.#store()).read(path);
  }

  async write(path: string, bytes: Uint8Array): Promise<void> {
    return (await this.#store()).write(path, bytes);
  }

  async remove(path: string): Promise<void> {
    return (await this.#store()).remove(path);
  }

  async removeDirectory(path: string): Promise<void> {
    return (await this.#store()).removeDirectory(path);
  }

  async move(from: string, to: string): Promise<void> {
    return (await this.#store()).move(from, to);
  }
}

/**
 * The Journal's byte store on this device.
 *
 * A factory rather than a module singleton, because {@link JournalScope} holds
 * exactly one for its lifetime and there is exactly one scope per document —
 * so the singleton would buy nothing and would outlive the tree that owns it,
 * which is what makes a remount reuse a handle the previous document resolved.
 */
export function openBrowserJournalFiles(): JournalFileStore {
  return new DeferredOpfsJournalFileStore();
}

/* ------------------------------------------------------------------------- *
 * The scope
 * ------------------------------------------------------------------------- */

export interface JournalScopeProps {
  /**
   * The byte substrate. Fixed for the lifetime of the scope: the repository is
   * built over it and a swap mid-flight would leave a verified write half
   * committed on one store and read back from another.
   */
  readonly files?: JournalFileStore;
  /**
   * Released on `foregroundLost`, from
   * `composeDeviceVerification`. Absent when nothing in this composition holds
   * key material — see that function for why a no-op is not supplied instead.
   */
  readonly dropKeyMaterial?: () => void;
  readonly children?: ReactNode;
}

export function JournalScope({
  files,
  dropKeyMaterial,
  children,
}: JournalScopeProps): ReactNode {
  const [store] = useState(() => files ?? openBrowserJournalFiles());
  const digest = useDigest();
  const clock = useClock();

  const repository = useMemo<JournalRepositoryPort>(
    () =>
      createJournalRepository({
        files: store,
        digest,
        // Staging file names only. Taken from the app's clock rather than
        // `Date.now` so a test that pins time gets deterministic names, and so
        // there is one answer in this document to what time it is.
        now: () => clock.now(),
      }),
    [clock, digest, store],
  );

  return (
    <JournalRepositoryProvider repository={repository}>
      <JournalPrivacyScope
        {...(dropKeyMaterial === undefined ? {} : { dropKeyMaterial })}
      >
        <JournalSurfaces repository={repository}>{children}</JournalSurfaces>
      </JournalPrivacyScope>
    </JournalRepositoryProvider>
  );
}

/**
 * The one shared privacy session, for the whole document.
 *
 * Five surfaces built by five packets call `usePrivacySession()` — Journal home,
 * the goals workspace, the composer, Settings' Journal privacy pane, and Train's
 * corner note — and every one of them reads the *shared* session on purpose: a
 * second provider would be a second lease and a second answer to "is the Journal
 * unlocked", and the surface that asked the wrong one prompts again, or worse,
 * does not prompt at all.
 *
 * `loadLibrary` comes from the Journal's own loader rather than being built
 * here. The repository belongs to the Journal, and a privacy layer that read
 * storage itself would be a privacy layer that could be made to read while
 * concealed.
 */
function JournalPrivacyScope({
  dropKeyMaterial,
  children,
}: {
  readonly dropKeyMaterial?: () => void;
  readonly children?: ReactNode;
}): ReactNode {
  const loadLibrary = useJournalLibraryLoader();
  return (
    <PrivacySessionProvider
      loadLibrary={loadLibrary}
      {...(dropKeyMaterial === undefined ? {} : { dropKeyMaterial })}
    >
      {children}
    </PrivacySessionProvider>
  );
}

function JournalSurfaces({
  repository,
  children,
}: {
  readonly repository: JournalRepositoryPort;
  readonly children?: ReactNode;
}): ReactNode {
  const identity = useJournalComposerIdentity();

  return (
    <JournalComposerSessionProvider repository={repository} identity={identity}>
      <JournalReaderPresentationProvider>
        <JournalGoalsPresentationProvider>
          <JournalHosts repository={repository}>{children}</JournalHosts>
        </JournalGoalsPresentationProvider>
      </JournalReaderPresentationProvider>
    </JournalComposerSessionProvider>
  );
}

/**
 * Names the provider that is missing, at the point the composition is wrong.
 *
 * The three presentation hooks are `useOptional…` because a *feature* may
 * legitimately render without them — a wide Journal workspace draws the reader
 * in its own column and never presents one. Here they cannot be absent: this
 * component is rendered by {@link JournalSurfaces} directly beneath all three,
 * so a null is a mistake in this file and nowhere else, and silently degrading
 * to an inert Journal would hide it behind copy that means something different.
 */
function required<T>(value: T | null, provider: string): T {
  if (value === null) {
    throw new Error(
      `<${provider}> is missing above the Journal hosts. JournalSurfaces mounts ` +
        "every Journal provider in order; this is a composition bug in app-state/journalHost.tsx.",
    );
  }
  return value;
}

function JournalHosts({
  repository,
  children,
}: {
  readonly repository: JournalRepositoryPort;
  readonly children?: ReactNode;
}): ReactNode {
  const session: JournalComposerSessionState = required(
    useOptionalJournalComposerSession(),
    "JournalComposerSessionProvider",
  );
  const reader: JournalReaderPresentationState = required(
    useOptionalJournalReaderPresentation(),
    "JournalReaderPresentationProvider",
  );
  const goals: JournalGoalsPresentationState = required(
    useOptionalJournalGoalsPresentation(),
    "JournalGoalsPresentationProvider",
  );
  const privacy = usePrivacySession();
  const clock = useClock();
  const exporter = useExporter();
  const ids = useIds();

  const library = privacy.library;
  const { refreshLibrary } = privacy;

  /**
   * F-JR-13. The reader is handed the *projection* the timeline already holds
   * (F-JR-43's draft-overlaid working truth), the goal the link resolves to
   * **now** rather than the title snapshot frozen into the entry (F-JR-18), and
   * the mutations it cannot perform itself.
   */
  const openReader = useCallback(
    (projection: JournalEntryProjection): void => {
      const goalId = projection.entry.goal?.goalID;
      const liveGoal =
        goalId === undefined
          ? null
          : (library?.goals.find((goal) => goal.id === goalId) ?? null);

      reader.present({
        projection,
        liveGoal,
        recoveries: library?.recoveries ?? [],
        repository,
        // Not dismissed first: `beginEditing` presents the composer over the
        // same single full-viewport slot, and dismissing here would clear the
        // path the composer is about to set. A refused gate therefore leaves
        // the reader where it was, which is the state the user can act on.
        onEdit: () => {
          session.beginEditing(projection.entry);
        },
        // The target state is ignored on purpose: `toggleJournalFavorite` flips
        // the flag on whichever record owns the newest truth, and a caller that
        // passed a desired value would be a second opinion about which record
        // that is. `false` snaps the reader's control back to the projection.
        onToggleFavorite: async (): Promise<boolean> => {
          const result = await toggleJournalFavorite(repository, projection, clock.now());
          if (result.kind !== "ok") return false;
          await refreshLibrary();
          return true;
        },
        onDelete: async (): Promise<boolean> => {
          const result = await deleteJournalEntry(repository, projection.entry);
          if (result.kind !== "ok") return false;
          await refreshLibrary();
          return true;
        },
        onDismiss: () => {
          // The entry may have been edited or favourited while it was open, and
          // the timeline behind it is rendered from the library, not from the
          // projection the reader held.
          void refreshLibrary();
        },
      });
    },
    [clock, library, reader, refreshLibrary, repository, session],
  );

  const exportRecovery = useCallback(
    async (item: JournalRecoveryItem): Promise<boolean> => {
      const result = await exportPreservedCopy({ repository, exporter, item });
      // F-JR-04's failure alert says the file could not be *verified* and that
      // nothing was shared or changed. A picker the user dismissed is neither,
      // so a cancelled export is reported as success and says nothing.
      return result.kind === "exported" && result.outcome !== "failed";
    },
    [exporter, repository],
  );

  const homeHost = useMemo<JournalHomeHost>(
    () => ({
      beginWriting: (seed) =>
        session.beginWriting({
          ...(seed.prompt === undefined ? {} : { prompt: seed.prompt }),
          ...(seed.goal === undefined ? {} : { goal: seed.goal }),
        }),
      resumeDraft: (draft) => session.resumeDraft(draft),
      openReader,
      openGoals: () => {
        goals.present({
          repository,
          newGoalId: () => ids.journalGoalId(),
          beginGoalReflection: (goal) => session.beginGoalReflection(goal),
          // No `onChanged`: `GoalsRoute` already re-reads the shared library
          // after every durable goal write, and a second refresh per write
          // would read the same files twice for one change.
        });
      },
      exportRecovery,
    }),
    [exportRecovery, goals, ids, openReader, repository, session],
  );

  const composerHost = useMemo<JournalComposerHost>(
    () => ({
      // F-JR-19: the composer must not be able to dismiss itself on an outcome
      // that kept it open, so the dismissal belongs to whoever opened it.
      onDismiss: () => {
        session.dismiss();
        // The entry that was just promoted is not in the library the timeline
        // is rendered from until it is re-read.
        void refreshLibrary();
      },
      // `pickMedia`, `recordVoiceRecap`, `chooseWritingPrompt` and
      // `openAttachment` belong to the media and prompt packets. Absent, each
      // of their controls renders as an inert "{title}, unavailable" label
      // (§8.6) rather than a button that would do nothing.
    }),
    [refreshLibrary, session],
  );

  return (
    <JournalComposerHostProvider host={composerHost}>
      <JournalHomeHostProvider host={homeHost}>{children}</JournalHomeHostProvider>
    </JournalComposerHostProvider>
  );
}
